import {
  AfterContentInit,
  AfterViewInit,
  Component,
  contentChild,
  ContentChild,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import ArrayStore from "devextreme/data/array_store";
import { PageDatagridColumnComponent } from "./page-datagrid-column.component";
import DevExpress from "devextreme";
import { PageDatagridDataSource } from "./page-datagrid-data-source";
import { DxDataGridComponent } from "devextreme-angular";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { PageDatagridToolbarComponent } from "./page-datagrid-toolbar.component";
import { Subscription } from "rxjs";
import { includes, trim } from "lodash-es";
import { PageDatagridColumnButtonComponent } from "./page-datagrid-column-button.component";
import { mapDxToolbarItems } from "../page.support";
import { PageDataGridEditComponent } from "./page-datagrid-edit.component";
import { PageDatagridColumnIconComponent } from "./page-datagrid-column-icon.component";
import { getViewport, isViewportLess } from "src/app/support/viewport";
import DxDataGrid, {
  EditorPreparingEvent,
  OptionChangedEvent,
  RowClickEvent,
  RowExpandedEvent,
  RowExpandingEvent,
  SelectedFilterOperation,
  Toolbar
} from "devextreme/ui/data_grid";
import { ValueChangedEvent } from "devextreme/ui/text_box";
import { Mutex } from "async-mutex";
import { PageToolbarComponent } from "src/app/components/ui/page/page-shared/page-toolbar/page-toolbar.component";
import { createEvents, EventsEmitter } from "src/app/support/events";
import {
  IntegrationDropPoints,
  IntegrationLayoutDataGridColumn
} from "src/app/integrations/integration-layout";
import { INTEGRATION_EVENTS } from "src/app/integrations/integration-events";
import { IntegrationService } from "src/app/services/integration.service";
import { isNumber, sift } from "radash";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { PlatformType } from "src/app/common/platform-type";
import { isArray } from "es-toolkit/compat";
import { DxDataGridTypes } from "devextreme-angular/ui/data-grid";
import { TabService, TabServiceTab } from "src/app/services/tab.service";
import { PAGE_DATAGRID_TAB_SHELL } from "src/app/tokens/page-datagrid-tab-shell.token";

export type PageDataGridEditorPreparingOptions = {
  cancel: boolean;
  component: DxDataGrid;
  editorOptions: Record<string, any>;
  parentType: string;
  command?: string;
  row?: any;
};

@Component({
  standalone: false,
  selector: "pms-page-datagrid",
  templateUrl: "page-datagrid.component.html",
  styleUrl: "page-datagrid.component.scss"
})
export class PageDatagridComponent
  implements AfterContentInit, AfterViewInit, OnChanges
{
  @Input()
  keyExpr: string | string[] | undefined;

  @Input()
  remoteOperations: boolean = true;

  @Input()
  dataSource!: PageDatagridDataSource<any, any> | Array<unknown>;

  dataSourcePaginate = input<boolean>(true);

  @Input()
  showColumnLines: boolean = true;

  @Input()
  showRowLines: boolean = false;

  @Input()
  showFilterRow: boolean = true;

  @Input()
  showOperationChooser: boolean = false;

  @Input()
  showFilterPanel: boolean = false;

  @Input()
  columnHidingEnabled: boolean = false;

  @Input()
  enableMasterDetail: boolean = false;

  @Input()
  showMasterDetail: boolean = false;

  @Input()
  hideColumns: string[] = [];

  @Input()
  background: string | undefined;

  @Input()
  rowRequiresAttentionFn: ((data: any) => boolean) | undefined;

  @Input()
  rowAddCssClassesFn: ((data: any) => string | void) | undefined;

  @Input()
  selectAllMode: "page" | "allPages" = "page";

  @Input()
  allowSelectAll: boolean = true;

  @Input()
  hideSelectionCountMessage: boolean | undefined;

  @Input()
  selectionDeferred: boolean | undefined;

  @Input()
  resolveSelectionKeysOnChange: boolean = true;

  @Input()
  ngTemplate: TemplateRef<any> | null = null;

  readonly integrationDropPoint = input<IntegrationDropPoints | null>(null);
  readonly integrationEntity = input<unknown | null>(null);
  readonly integrationParamsBuilder = input<
    (() => Record<string, unknown>) | null
  >(null);

  @Input()
  onEditorPreparing:
    | ((params: PageDataGridEditorPreparingOptions) => void)
    | undefined;

  @Input()
  hidePager: boolean = false;

  @Input()
  pageSize: number = 25;

  @Input()
  pagerAllowedPageSizes: number[] = [10, 25, 50];

  @Input()
  pagerShowPageSizeSelector: boolean = true;

  @Input()
  scrollingUseNative: boolean | "auto" = "auto";

  @Input()
  scrollingShowScrollbar: boolean = true;

  @Input()
  hideBorders: boolean = false;

  @Input()
  disableSorting: boolean = false;

  @Input()
  disableAbsoluteHeight: boolean = false;

  @Input()
  disableSelection: boolean = false;

  @Input()
  disablePagination: boolean = false;

  @Input()
  selectedRowKeys: string[] = [];

  @Input()
  noPadding: boolean = false;

  noMargin = input<boolean>(false);

  @Input()
  rounded: boolean = false;

  @Input()
  rowCssClasses: string = "";

  @Input()
  noDataText: string = "";

  dxToolbar = input<Toolbar | undefined>(undefined, {
    alias: "toolbar"
  });

  @Output()
  rowClick: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  selectionChanged: EventEmitter<any> = new EventEmitter<{
    keys: any[];
    rows: any[];
  }>();

  @Output()
  rowPrepared: EventEmitter<any> = new EventEmitter();

  @Output()
  rowExpanding: EventEmitter<RowExpandingEvent> =
    new EventEmitter<RowExpandingEvent>();

  @Output()
  rowExpanded: EventEmitter<RowExpandedEvent> =
    new EventEmitter<RowExpandedEvent>();

  @Output()
  optionChanged: EventEmitter<OptionChangedEvent> =
    new EventEmitter<OptionChangedEvent>();

  @ContentChild(PageDataGridEditComponent)
  edit: PageDataGridEditComponent | null = null;

  @ContentChild(PageDatagridToolbarComponent)
  toolbar: PageDatagridToolbarComponent | null = null;

  toolbarV2 = contentChild(PageToolbarComponent);

  summary = input<DxDataGridTypes.Summary | undefined>();

  @ContentChildren(PageDatagridColumnButtonComponent)
  columnButtons!: QueryList<PageDatagridColumnButtonComponent>;

  @ContentChildren(PageDatagridColumnComponent)
  columns!: QueryList<PageDatagridColumnComponent>;

  @ViewChild(DxDataGridComponent)
  dxDataGrid!: DxDataGridComponent;

  dxDataGridColumns: DevExpress.ui.dxDataGrid.Column[] = [];
  dxToolbarItems: DevExpress.ui.dxToolbar.Item[] = [];
  dxDataGridEditing: {
    allowAdding?: boolean;
    allowUpdating?: boolean;
    allowDeleting?: boolean;
    mode?: "batch" | "cell" | "row" | "form" | "popup";
  } = {};

  reshapeOnPush = input<boolean>(false);

  repaintChangesOnly = input<boolean>(false);

  loadPanelDisabled = input<boolean>(false);

  readonly eventsEmitter: EventsEmitter = createEvents();

  protected readonly PLATFORM = inject(PLATFORM_TOKEN);
  protected readonly PlatformType = PlatformType;

  private toolbarActionsSubscription: Subscription | null = null;

  private readonly mutexReload: Mutex = new Mutex();

  private readonly integrationService = inject(IntegrationService);

  readonly #tabService = inject(TabService);

  readonly #tabShellContext = inject(PAGE_DATAGRID_TAB_SHELL, {
    optional: true
  });

  #tabActivationEndSubscription: string | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.#tabActivationEndSubscription) {
        this.#tabService.removeEventListener(
          this.#tabActivationEndSubscription
        );
      }

      this.toolbarActionsSubscription?.unsubscribe();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dataSourceChange = changes["dataSource"];

    if (dataSourceChange && !dataSourceChange.firstChange) {
      this.dxDataGrid.instance.option(
        "dataSource",
        getDataSource({
          keyExpr: this.keyExpr,
          pageDataSource: this.dataSource,
          dataSourcePaginate: this.dataSourcePaginate(),
          reshapeOnPush: this.reshapeOnPush()
        })
      );
    }
  }

  ngAfterContentInit(): void {
    this.toolbarActionsSubscription =
      this.toolbar?.actionItems.changes.subscribe((value) => {
        this.dxToolbarItems = mapDxToolbarItems({
          actionItems: value.toArray(),
          selectItems: <any>this.toolbar?.selectItems?.toArray() || [],
          textItems: <any>this.toolbar?.textItems?.toArray() || [],
          selectedKeys: this.dxDataGrid.selectedRowKeys
        });
      }) || null;

    this.dxToolbarItems = mapDxToolbarItems({
      actionItems: <any>this.toolbar?.actionItems.toArray() || [],
      selectItems: <any>this.toolbar?.selectItems?.toArray() || [],
      textItems: <any>this.toolbar?.textItems?.toArray() || [],
      selectedKeys: []
    });

    this.dxDataGridColumns = this.mapDxDatagridColumns(
      this.columnButtons.toArray(),
      this.columns.toArray(),
      this.hideColumns
    );

    if (this.edit) {
      this.dxDataGridEditing = {
        allowAdding: this.edit.allowAdding,
        allowUpdating: this.edit.allowUpdating,
        allowDeleting: this.edit.allowDeleting,
        mode: this.edit.mode
      };
    }
  }

  ngAfterViewInit(): void {
    const tabForActivation: TabServiceTab | undefined =
      this.#tabShellContext?.tab;

    if (tabForActivation) {
      this.#tabActivationEndSubscription = this.#tabService.addEventListener({
        tab: tabForActivation,
        type: "activationEnd",
        handler: () => {
          this.dxDataGrid.instance.refresh();
        }
      });
    }

    this.dxDataGrid.instance.option({
      onRowPrepared: ({ rowElement, rowType, data }) => {
        if (rowType === "data") {
          if (this.rowCssClasses) {
            rowElement.classList.add(...this.rowCssClasses.split(" "));
          }

          if (this.rowRequiresAttentionFn) {
            if (this.rowRequiresAttentionFn(data)) {
              rowElement.classList.add("border-l-4", "border-l-orange-400");
            }
          }

          if (this.rowAddCssClassesFn) {
            const customRowCss = this.rowAddCssClassesFn(data);
            if (!!customRowCss) {
              rowElement.classList.add(...customRowCss.split(" "));
            }
          }
        }
      }
    });

    this.#enableSingleChoiceHeaderFilters();

    this.dxDataGrid.dataSource = getDataSource({
      keyExpr: this.keyExpr,
      pageDataSource: this.dataSource,
      dataSourcePaginate: this.dataSourcePaginate(),
      reshapeOnPush: this.reshapeOnPush()
    });
  }

  onDataGridInitialized(): void {
    this.#enableSingleChoiceHeaderFilters();
  }

  #singleChoiceHeaderFiltersEnabled = false;

  #mapColumnHeaderFilter(
    headerFilter: PageDatagridColumnComponent["headerFilter"]
  ): PageDatagridColumnComponent["headerFilter"] {
    if (!headerFilter) {
      return undefined;
    }

    if (!headerFilter.singleChoice) {
      return headerFilter;
    }

    return {
      ...headerFilter,
      allowSelectAll: false
    };
  }

  #enableSingleChoiceHeaderFilters(): void {
    if (this.#singleChoiceHeaderFiltersEnabled) {
      return;
    }

    const grid = this.dxDataGrid?.instance as
      | (DxDataGrid & {
          getView: (name: string) => {
            showHeaderFilterMenu: (
              $columnElement: unknown,
              options: unknown
            ) => void;
            getListComponent: () => {
              option: {
                (name: string): unknown;
                (name: string, value: unknown): void;
              };
            } | null;
            getPopupContainer: () => {
              option: {
                (name: string): unknown;
                (name: string, value: unknown): void;
              };
            } | null;
          };
        })
      | undefined;

    if (!grid) {
      return;
    }

    const headerFilterView = grid.getView("headerFilterView");

    if (!headerFilterView?.showHeaderFilterMenu) {
      return;
    }

    this.#singleChoiceHeaderFiltersEnabled = true;

    const originalShowHeaderFilterMenu =
      headerFilterView.showHeaderFilterMenu.bind(headerFilterView);

    headerFilterView.showHeaderFilterMenu = (
      $columnElement: unknown,
      options: unknown
    ): void => {
      originalShowHeaderFilterMenu($columnElement, options);

      if (!this.#isSingleChoiceHeaderFilter(options)) {
        return;
      }

      this.#configureSingleChoiceHeaderFilterList(headerFilterView);

      const popup = headerFilterView.getPopupContainer();

      if (!popup) {
        return;
      }

      const previousOnShown = popup.option("onShown") as
        | ((event: unknown) => void)
        | undefined;

      popup.option("onShown", (event: unknown) => {
        previousOnShown?.(event);
        this.#configureSingleChoiceHeaderFilterList(headerFilterView);
      });
    };
  }

  #configuredSingleChoiceLists = new WeakSet<object>();

  #configureSingleChoiceHeaderFilterList(headerFilterView: {
    getListComponent: () => {
      option: {
        (name: string): unknown;
        (name: string, value: unknown): void;
      };
    } | null;
  }): void {
    const list = headerFilterView.getListComponent();

    if (!list) {
      return;
    }

    list.option("selectionMode", "single");

    if (this.#configuredSingleChoiceLists.has(list)) {
      return;
    }

    this.#configuredSingleChoiceLists.add(list);

    let selectedValue = this.#getHeaderFilterSelectedValue(list);

    const previousOnContentReady = list.option("onContentReady") as
      | ((event: {
          component: {
            option: {
              (name: string): unknown;
              (name: string, value?: unknown): unknown;
            };
          };
        }) => void)
      | undefined;

    list.option(
      "onContentReady",
      (event: {
        component: {
          option: {
            (name: string): unknown;
            (name: string, value?: unknown): unknown;
          };
        };
      }) => {
        previousOnContentReady?.(event);
        event.component.option("selectionMode", "single");
        selectedValue = this.#getHeaderFilterSelectedValue(event.component);
      }
    );

    const previousOnItemClick = list.option("onItemClick") as
      | ((event: {
          itemData: unknown;
          component: {
            option: {
              (name: string): unknown;
              (name: string, value?: unknown): unknown;
            };
          };
        }) => void)
      | undefined;

    list.option(
      "onItemClick",
      (event: {
        itemData: unknown;
        component: {
          option: {
            (name: string): unknown;
            (name: string, value?: unknown): unknown;
          };
        };
      }) => {
        previousOnItemClick?.(event);

        const clickedValue = this.#getHeaderFilterItemValue(event.itemData);

        if (
          selectedValue !== undefined &&
          selectedValue !== null &&
          selectedValue === clickedValue
        ) {
          event.component.option("selectedItems", []);
          selectedValue = null;

          return;
        }

        selectedValue = clickedValue;
      }
    );
  }

  #getHeaderFilterSelectedValue(list: {
    option: (name: string) => unknown;
  }): unknown {
    const selectedItems = list.option("selectedItems") as unknown[] | undefined;

    if (!selectedItems?.length) {
      return null;
    }

    return this.#getHeaderFilterItemValue(selectedItems[0]);
  }

  #getHeaderFilterItemValue(item: unknown): unknown {
    if (item && typeof item === "object" && "value" in item) {
      return (item as { value: unknown }).value;
    }

    return item;
  }

  #isSingleChoiceHeaderFilter(options: unknown): boolean {
    const headerFilterOptions = options as {
      dataField?: string;
      headerFilter?: { singleChoice?: boolean };
    };

    if (headerFilterOptions.headerFilter?.singleChoice) {
      return true;
    }

    const dataField = headerFilterOptions.dataField;

    if (!dataField) {
      return false;
    }

    return this.columns
      .toArray()
      .some(
        (column) =>
          column.dataField === dataField && !!column.headerFilter?.singleChoice
      );
  }

  private mapDxDatagridColumns(
    buttons: PageDatagridColumnButtonComponent[],
    columns: Array<
      PageDatagridColumnComponent | PageDatagridColumnIconComponent
    >,
    hideColumns: string[]
  ): DevExpress.ui.dxDataGrid.Column[] {
    let integrationColumns: Array<{
      dxColumn: DevExpress.ui.dxDataGrid.Column;
      integrationColumn: IntegrationLayoutDataGridColumn;
    }> = [];

    if (this.integrationDropPoint()?.length || 0 > 0) {
      integrationColumns = this.integrationService
        .getDataGridColumnsByDropPoint(this.integrationDropPoint()!)
        .map((integrationColumn: IntegrationLayoutDataGridColumn) => {
          return {
            integrationColumn,
            dxColumn: <DevExpress.ui.dxDataGrid.Column>{
              name: integrationColumn.name,
              dataField: integrationColumn.dataField,
              allowFiltering: false,
              allowEditing: false,
              allowSearch: false,
              width: integrationColumn.width,
              cssClass: integrationColumn.cssClass,
              caption: integrationColumn.caption(),
              cellTemplate: (
                cellElement: HTMLElement,
                cellInfo: DevExpress.ui.dxDataGrid.ColumnCellTemplateData
              ) => {
                return integrationColumn.cellTemplate(
                  cellElement,
                  cellInfo.data
                );
              }
            }
          };
        });
    }

    const currentViewPort = getViewport();

    const dataColumns: DevExpress.ui.dxDataGrid.Column[] = sift(
      columns
        .filter(
          (
            column:
              | PageDatagridColumnComponent
              | PageDatagridColumnIconComponent
          ) => {
            if (!column.hideOnPlatforms) {
              return true;
            }

            return !includes(column.hideOnPlatforms, this.PLATFORM);
          }
        )
        .filter(
          (
            column:
              | PageDatagridColumnComponent
              | PageDatagridColumnIconComponent
          ) => {
            if (!column.name || hideColumns.length === 0) {
              return true;
            }

            return !includes(hideColumns, column.name);
          }
        )
        .filter((column) => {
          if (!column.hideOnViewport) {
            return true;
          }

          return isViewportLess(column.hideOnViewport, currentViewPort);
        })
        .map((x) => {
          switch (true) {
            case x instanceof PageDatagridColumnIconComponent: {
              const columnIcon: PageDatagridColumnIconComponent = <
                PageDatagridColumnIconComponent
              >x;
              return {
                name: columnIcon.name,
                dataField: columnIcon.dataField,
                allowFiltering: false,
                allowEditing: false,
                allowSearch: false,
                alignment: "center",
                cssClass: "align-middle",
                hidingPriority: columnIcon.hidingPriority,
                cellTemplate: (cellElement, cellInfo) => {
                  if (columnIcon.icon) {
                    const icon = columnIcon.icon(cellInfo.data);
                    const textTile = columnIcon.tooltip
                      ? columnIcon.tooltip(cellInfo.data)
                      : columnIcon.title;
                    if (icon) {
                      cellElement.innerHTML = `<div><i class="${columnIcon.icon(
                        cellInfo.data
                      )}" title="${textTile}"></i></div>`;
                    }
                  }
                },
                width: 22
              };
            }
            case x instanceof PageDatagridColumnComponent: {
              const column = <PageDatagridColumnComponent>x;

              return {
                dataField: column.dataField,
                caption: column.caption,
                calculateCellValue: column.calculateCellValue,
                calculateDisplayValue: column.calculateDisplayValue,
                allowFiltering: column.allowFiltering,
                allowSorting: column.allowSorting,
                allowHeaderFiltering: column.allowHeaderFiltering,
                filterOperations: column.filterOperations,
                selectedFilterOperation: <SelectedFilterOperation | undefined>(
                  column.selectedFilterOperation
                ),
                filterPlaceholder: column.filterPlaceholder(),
                filterValue: column.filterValue,
                filterValues: column.filterValues,
                filterType: column.filterType,
                headerFilter: this.#mapColumnHeaderFilter(column.headerFilter),
                dataType: column.dataType,
                width: column.width,
                minWidth: column.minWidth,
                cssClass: trim(
                  `${column.cssClassCursor} ${column.cssClass || ""}`
                ),
                alignment: column.alignment,
                hidingPriority: column.hidingPriority,
                fixed: column.fixed,
                fixedPosition: column.fixedPosition,
                visible: column.visible,
                cellTemplate: column.cellTemplate
                  ? (cellElement, cellInfo) => {
                      return column.cellTemplate!.render({
                        container: cellElement,
                        row: cellInfo.data
                      });
                    }
                  : undefined,
                groupIndex: column.groupIndex
              };
            }
            default:
              return null;
          }
        })
    );

    const buttonsColumns: DevExpress.ui.dxDataGrid.Column[] =
      buttons.length === 0
        ? []
        : [
            <DevExpress.ui.dxDataGrid.Column>{
              type: "buttons",
              alignment: "center",
              cssClass: "align-middle",
              buttons: buttons.map((x) => ({
                icon: x.icon,
                onClick: (
                  event: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent
                ) => x.onClick.emit(event.row?.data),
                name: x.name,
                disabled: ({ row: { data } }: any) => {
                  return x.disabled ? x.disabled(data) : false;
                }
              }))
            }
          ];

    const dataAndButtonColumns: DevExpress.ui.dxDataGrid.Column[] = [
      ...dataColumns,
      ...buttonsColumns
    ];

    integrationColumns.forEach(({ dxColumn, integrationColumn }) => {
      if (isNumber(integrationColumn.index)) {
        dataAndButtonColumns.splice(integrationColumn.index!, 0, dxColumn);
      } else {
        dataAndButtonColumns.push(dxColumn);
      }
    });

    return dataAndButtonColumns;
  }

  async onSelectionChanged(data: {
    selectedRowKeys: any[];
    selectedRowsData: any[];
  }): Promise<void> {
    const selectedRowKeys = data.selectedRowKeys;
    const selectedRowsData = data.selectedRowsData;

    this.selectionChanged.emit({
      keys: selectedRowKeys,
      data: selectedRowsData
    });

    if (!this.hideSelectionCountMessage) {
      this.dxToolbarItems = mapDxToolbarItems({
        actionItems: <any>this.toolbar?.actionItems.toArray() || [],
        selectItems: <any>this.toolbar?.selectItems.toArray() || [],
        textItems: <any>this.toolbar?.textItems.toArray() || [],
        selectedKeys: selectedRowKeys
      });
    }

    if (this.selectionDeferred && this.resolveSelectionKeysOnChange) {
      const deferredSelectedRowKeys =
        await this.dxDataGrid.instance.getSelectedRowKeys();

      this.eventsEmitter.emit(
        INTEGRATION_EVENTS.PAGE_SELECTION_CHANGED,
        deferredSelectedRowKeys || []
      );
    } else {
      this.eventsEmitter.emit(
        INTEGRATION_EVENTS.PAGE_SELECTION_CHANGED,
        selectedRowKeys || []
      );
    }
  }

  dataGridOnEditorPreparing(
    dxEvent: EditorPreparingEvent & { filterPlaceholder?: string }
  ): void {
    if (this.onEditorPreparing) {
      this.onEditorPreparing(dxEvent);
    }

    const columnWithFilterLimit: Array<{
      dataField: string;
      minLength: number;
    }> = this.columns
      .toArray()
      .filter((column) => {
        if (!column.allowFiltering) {
          return false;
        }

        return true;
      })
      .map((column) => {
        return {
          dataField: column.dataField,
          minLength: column.filterMinLength || 0
        };
      });

    if (
      columnWithFilterLimit
        .map((x) => x.dataField)
        .includes(dxEvent.dataField || "") &&
      dxEvent.parentType === "filterRow"
    ) {
      dxEvent.editorOptions.valueChangeEvent = "keyup";
      dxEvent.editorOptions.placeholder = dxEvent.filterPlaceholder;
      const defaultValueChangeHandler = dxEvent.editorOptions.onValueChanged;

      dxEvent.editorOptions.onValueChanged = (
        dxValueChangedEvent: ValueChangedEvent
      ) => {
        const minLength =
          columnWithFilterLimit.find((x) => x.dataField === dxEvent.dataField)
            ?.minLength || 0;

        if (
          dxValueChangedEvent.value &&
          dxValueChangedEvent.value.length < minLength
        ) {
          dxValueChangedEvent.event?.preventDefault();
        } else {
          defaultValueChangeHandler(dxValueChangedEvent);
        }
      };
    }
  }

  async reload(): Promise<void> {
    await this.mutexReload.runExclusive(async () => {
      await this.dxDataGrid?.instance.refresh();
    });
  }

  getStore(): CustomStore | undefined {
    return <CustomStore>this.dxDataGrid.instance.getDataSource()?.store();
  }

  onRowClick(rowClickEvent: RowClickEvent): void {
    // Verifica se l'elemento cliccato si trova all'interno della colonna di espansione
    // Utilizza .closest() per vedere se il target o i suoi genitori hanno la classe specifica
    if (rowClickEvent.event?.target.closest(".dx-command-expand")) {
      return; // Esci dalla funzione senza fare nulla
    }

    this.rowClick.emit(rowClickEvent.data);
  }
}

function getDataSource({
  keyExpr,
  pageDataSource,
  dataSourcePaginate,
  reshapeOnPush
}: {
  keyExpr: string | string[] | undefined;
  pageDataSource: PageDatagridDataSource<any> | Array<unknown>;
  dataSourcePaginate: boolean;
  reshapeOnPush: boolean;
}): DataSource {
  let store: CustomStore | ArrayStore;

  switch (true) {
    case isArray(pageDataSource): {
      store = new ArrayStore({
        data: pageDataSource
      });
      break;
    }
    default: {
      store = new CustomStore({
        key: keyExpr || "id",
        load: async (options) => {
          return await pageDataSource.load(<any>options);
        },
        insert: async (values) => {
          if (!pageDataSource.insert) {
            throw new Error("insert undefined!");
          }

          return await pageDataSource.insert(values);
        },
        update: async (key: string, values) => {
          if (!pageDataSource.update) {
            throw new Error("update undefined");
          }

          return await pageDataSource.update(key, values);
        },
        remove: async (key: string) => {
          if (!pageDataSource.remove) {
            throw new Error("remove undefined");
          }

          return await pageDataSource.remove(key);
        }
      });
      break;
    }
  }

  return new DataSource({
    reshapeOnPush: reshapeOnPush,
    pushAggregationTimeout: 500,
    paginate: dataSourcePaginate,
    store
  });
}
